/**
 * @file index.jsx
 * @author Archana
 * @date 2024-10-28
 * @description Show & edit the candidate details.
 */
import { Box, Divider, Skeleton, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import { DetailsPanel } from "./DetailsPanel";
import { useFetchData } from "../../../components/hooks/useFetchData";
import Drawer from "../../CandidateDashboardPage/Drawer";
import axios from "axios";

const Lifecycle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [minLoading, setMinLoading] = React.useState(true);
  const [lead, setLead] = useState([]);
  const { fetchData, loading } = useFetchData();
  const { enqueueSnackbar } = useSnackbar();
  const [lifecycleEvents, setLifecycleevents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [candidateEvents, setCandidateEvents] = useState([]);
  const [value, setValue] = useState(0);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [set1, setOne] = useState([]);
  const [set2, setTwo] = useState([]);
  const [file, setFile] = useState(null);
  const [countryname, setCountryName] = useState("");
  const [coursename, setCourseName] = useState("");
  const [isPaymentPopupOpen, setIsPaymentPopupOpen] = useState(false);
  const [installments, setInstallments] = useState([]);
  const baseUrl = process.env.REACT_APP_API_URL;

  const toggleDrawer = async () => {
    await getDocumentsByCourse();
    setIsDrawerOpen(!isDrawerOpen);
  };

  const getDocumentsByCourse = async () => {
    let countryid = lead.coursecountry[0].countryid;
    let courseid = lead.coursecountry[0].courses[0];
    const body = { courseid: courseid, countryid: countryid, candidateid: id };
    const result = await fetchData("getdocumentsbycoursestocandidate", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      setOne(result.data.set1);
      setTwo(result.data.set2);
    }
  };

  const getCandidateInstallments = async () => {
    const body = { candidateid: id };
    const result = await fetchData("getcandidateinstallments", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      setInstallments(result.data.installments);
    }
  };

  const handleLifecycleChange = (value) => {
    setSelectedEvent(value);
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const getCandidateDetails = async () => {
    const body = {
      candidateuuid: id,
    };
    const result = await fetchData("getcandidatedetails", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      setCountryName(result.data.coursecountry[0].countryname);
      setCourseName(result.data.coursecountry[0].coursename);
      setLead(result.data);
    }
  };

  const getLifecycleEvents = async () => {
    const result = await fetchData(
      `getlifecycleventswithinpayment?candidateid=${id}`
    );
    if (result?.code === 200) {
      setLifecycleevents(result.data.lifecycleEventsWithinPayment);
    }
  };

  const handleLifecycleSave = async () => {
    if (!selectedEvent) return;
    const body = { candidateid: lead.id, lifecycleid: selectedEvent };
    const result = await fetchData("savelifecycleevent", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      setSelectedEvent("");
      getCandidateLifecyclEevents();
      getLifecycleEvents();
      enqueueSnackbar(result.message || "Successfull!", {
        variant: "success",
      });
    } else {
      enqueueSnackbar(result.message || "Something went wrong!", {
        variant: "error",
      });
    }
  };

  const getCandidateLifecyclEevents = async () => {
    const body = { candidateid: id };
    const result = await fetchData("getcandidatelifecycleevents", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      setCandidateEvents(result.data);
    }
  };

  const handleUpload = async (file, docid) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post(
        baseUrl + "savedocumentsofcandidate",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            candidateuuid: id,
            documentid: docid,
          },
        }
      );
      if (response.data.code === 200) {
        getDocumentsByCourse();
        enqueueSnackbar(response.data.message || "Successfull!", {
          variant: "success",
        });
      } else {
        enqueueSnackbar(response.data.message || "Something went wrong!", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  const handlePaymentPopup = () => {
    setIsPaymentPopupOpen(true);
  };

  const submitPayment = async (data) => {
    const result = await fetchData("confirmpayment", {
      method: "post",
      body: {
        ...data,
        candidateid: id,
      },
    });
    if (result?.code === 200) {
      getCandidateInstallments();
      enqueueSnackbar(result.message || "Successfull!", {
        variant: "success",
      });
    } else {
      enqueueSnackbar(result?.message || "Something went wrong!", {
        variant: "error",
      });
    }
  };

  const handleClosePopup = () => {
    setIsPaymentPopupOpen(false);
  };

  React.useEffect(() => {
    const fetchDataAsync = async () => {
      await getCandidateDetails();
      await getLifecycleEvents();
      await getCandidateLifecyclEevents();
      await getCandidateInstallments();
    };

    fetchDataAsync();
    const minLoadingTimer = setTimeout(() => {
      setMinLoading(false);
    }, 1500);

    return () => clearTimeout(minLoadingTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoading =
    minLoading ||
    loading["getcountries?showActive=1"] ||
    loading["getcourses?showActive=1"] ||
    loading["getcandidatedetails"];

  return (
    <>
      <Box
        display="flex"
        flex={1}
        flexDirection="column"
        overflow={{ xs: "auto", md: "hidden" }}
      >
        <DetailsPanel
          lead={lead}
          isLoading={isLoading}
          value={value}
          handleChange={handleChange}
          handleLifecycleChange={handleLifecycleChange}
          lifecycleEvents={lifecycleEvents}
          selectedEvent={selectedEvent}
          handleLifecycleSave={handleLifecycleSave}
          candidateEvents={candidateEvents}
          toggleDrawer={toggleDrawer}
          candidateuuid={id}
          handlePaymentPopup={handlePaymentPopup}
          handleClosePopup={handleClosePopup}
          isPaymentPopupOpen={isPaymentPopupOpen}
          installments={installments}
          submitPayment={submitPayment}
          getCandidateLifecyclEevents={getCandidateLifecyclEevents}
          countryname={countryname}
          coursename={coursename}
        />

        <Drawer
          isOpen={isDrawerOpen}
          onClose={toggleDrawer}
          set1={set1}
          set2={set2}
          handleUpload={handleUpload}
          candidateuuid={id}
          baseUrl={baseUrl}
        />

        <Stack
          flex={1}
          display="flex"
          columnGap={1}
          flexDirection="row"
          overflow="hidden"
          pt={1}
        >
          <Stack
            flex={1}
            overflow="hidden"
            rowGap={1}
            display="flex"
            flexDirection="column"
          ></Stack>
        </Stack>
      </Box>
    </>
  );
};

export default Lifecycle;
